import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import { getErrorMessage } from "../../common";
import { StudentDocumentDTO } from "../../models/EnrollmentHistoryDTO";
import { formatLocalDateTime } from "../../components/enrollment/format";
import ContractHistory from "../../components/enrollment/contractHistory";

export interface StudentDocumentsTabProps {
	studentId: number;
	campusId: number;
	canUpload: boolean;
	/** Small block for the profile's left column: list + a toggle for the upload form. */
	compact?: boolean;
}

const ACCEPTED_FILES = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif";

/** "Documentos" tab: ID copies of the student and guardian, contracts and other files attached to the profile. */
const StudentDocumentsTab: React.FC<StudentDocumentsTabProps> = ({ studentId, campusId, canUpload, compact }) => {
	const [showUpload, setShowUpload] = useState<boolean>(false);
	const http = HttpManager.getInstance();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [types, setTypes] = useState<{ [typeId: string]: string }>({});
	const [documents, setDocuments] = useState<StudentDocumentDTO[] | null>(null);
	const [typeId, setTypeId] = useState<number>(1);
	const [description, setDescription] = useState<string>("");
	const [file, setFile] = useState<File | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(() => {
		setError(null);
		http.studentService.getStudentDocuments(campusId, studentId)
			.then((response) => { setTypes(response.data.types); setDocuments(response.data.documents); })
			.catch((e) => { setDocuments([]); setError(getErrorMessage(e)); });
	}, [campusId, studentId]);

	useEffect(() => { load(); }, [load]);

	const upload = async () => {
		if (!file) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			await http.studentService.uploadStudentDocument(campusId, studentId, typeId, description, file);
			setFile(null);
			setDescription("");
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			setShowUpload(false);
			load();
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const view = async (document: StudentDocumentDTO) => {
		setError(null);
		try {
			const response = await http.studentService.downloadStudentDocument(campusId, studentId, document.id);
			const url = window.URL.createObjectURL(response.data);
			window.open(url, "_blank");
			setTimeout(() => window.URL.revokeObjectURL(url), 60000);
		} catch (e) {
			setError(getErrorMessage(e));
		}
	};

	const remove = async (document: StudentDocumentDTO) => {
		if (!window.confirm(`${Constants.Common.confirmDeleteDocument} (${document.type}: ${document.name})`)) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			await http.studentService.deleteStudentDocument(campusId, studentId, document.id);
			load();
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	//Contrato generado al matricular: aquí se sube (o reemplaza) la versión que firmó el cliente (Esteban, 2026-09-26).
	const uploadSigned = async (document: StudentDocumentDTO, signedFile: File) => {
		setBusy(true);
		setError(null);
		try {
			const response = await http.studentService.uploadSignedContract(campusId, studentId, document.id, signedFile);
			setTypes(response.data.types);
			setDocuments(response.data.documents);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const signedButton = (document: StudentDocumentDTO, pill: boolean) => document.contract && document.contract.canUploadSigned ? (
		<label className={pill ? "btn btn-outline-primary btn-sm rounded-pill" : "btn btn-link btn-sm"}
			style={{ position: "relative", padding: pill ? undefined : 0, margin: 0, fontSize: pill ? undefined : 12, whiteSpace: "nowrap" }}>
			<FontAwesomeIcon icon={solid("file-signature")} />&nbsp;{document.contract.signed ? "Reemplazar firmado" : "Subir firmado"}
			<input type="file" accept={ACCEPTED_FILES} hidden disabled={busy}
				onChange={(event) => {
					const signedFile = event.target.files && event.target.files[0];
					event.target.value = "";
					if (signedFile) { uploadSigned(document, signedFile); }
				}} />
		</label>
	) : "";

	const viewVersion = async (document: StudentDocumentDTO, versionId: number) => {
		setError(null);
		try {
			const response = await http.studentService.downloadContractVersion(campusId, studentId, document.id, versionId);
			const url = window.URL.createObjectURL(response.data);
			window.open(url, "_blank");
			setTimeout(() => window.URL.revokeObjectURL(url), 60000);
		} catch (e) {
			setError(getErrorMessage(e));
		}
	};

	const history = (document: StudentDocumentDTO) => document.contract && (document.contract.versions || []).length > 0
		? <ContractHistory versions={document.contract.versions} onOpen={(v) => viewVersion(document, v.id)} />
		: "";

	const typeText = (document: StudentDocumentDTO) => document.contract && document.contract.signed ? "Contrato firmado" : document.type;

	const sizeText = (size: number | null): string => {
		if (!size) {
			return "";
		}
		return size > 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`;
	};

	return (
		<div style={{ padding: compact ? 0 : "10px 5px", textAlign: "left" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: compact ? 4 : 10 }}>
				<h6 style={{ margin: 0, fontSize: compact ? 13 : undefined, color: compact ? "var(--wa-apagado)" : undefined }}>{Constants.Common.enrollmentDocuments}</h6>
				{compact && canUpload ? (
					<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 12 }} disabled={busy} onClick={() => setShowUpload(!showUpload)}>
						<FontAwesomeIcon icon={showUpload ? solid("chevron-up") : solid("upload")} />&nbsp;{Constants.Common.uploadDocument}
					</button>
				) : ""}
			</div>
			{canUpload && (!compact || showUpload) ? (
				<div className="card" style={{ marginBottom: 12 }}>
					<div className="card-body" style={{ padding: "10px 14px" }}>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
							<div style={{ flex: "1 1 200px" }}>
								<label style={{ position: "relative", display: "block", fontSize: 13 }}>{Constants.Common.documentType}</label>
								<select className="form-select form-select-sm" value={typeId} disabled={busy} onChange={(event) => setTypeId(+event.target.value)}>
									{Object.keys(types).map((key) => <option key={"dt_" + key} value={key}>{types[key]}</option>)}
								</select>
							</div>
							<div style={{ flex: "1 1 200px" }}>
								<label style={{ position: "relative", display: "block", fontSize: 13 }}>{Constants.Common.documentDescription}</label>
								<input className="form-control form-control-sm" type="text" maxLength={250} value={description} disabled={busy}
									onChange={(event) => setDescription(event.target.value)} />
							</div>
							<div style={{ flex: "1 1 240px" }}>
								<label style={{ position: "relative", display: "block", fontSize: 13 }}>{Constants.Common.documentFile}</label>
								<input ref={fileInputRef} className="form-control form-control-sm" type="file" accept={ACCEPTED_FILES} disabled={busy}
									onChange={(event) => setFile(event.target.files && event.target.files.length > 0 ? event.target.files[0] : null)} />
							</div>
							<div style={{ flex: "0 0 auto" }}>
								<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !file} onClick={upload}>
									<FontAwesomeIcon icon={solid("upload")} />&nbsp;{Constants.Common.uploadDocument}
								</button>
							</div>
						</div>
					</div>
				</div>
			) : ""}
			{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
			{documents === null ? <div className="loader" /> : (documents.length < 1
				? <div style={{ padding: compact ? "4px 0" : 15, color: "var(--wa-apagado)", fontSize: compact ? 12 : undefined }}>{compact ? "-" : Constants.Common.noDocuments}</div>
				: compact ? (
					<ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
						{documents.map((document) => (
							<li key={"cdoc_" + document.id} style={{ padding: "3px 0", borderBottom: "1px solid #EEE" }}>
							<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
								<span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${document.type}: ${document.name}${document.description ? " · " + document.description : ""}`}>
									<b>{typeText(document)}</b> · {document.name}
								</span>
								{signedButton(document, false)}
								<button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busy} title={Constants.Common.view} onClick={() => view(document)}>
									<span className="waVer">Ver</span>
								</button>
								{canUpload ? (
									<button type="button" className="btn btn-link btn-sm text-danger" style={{ padding: 0 }} disabled={busy} title={Constants.Common.delete} onClick={() => remove(document)}>
										<FontAwesomeIcon icon={solid("trash")} />
									</button>
								) : ""}
							</div>
							{history(document)}
							</li>
						))}
					</ul>
				) : (
					<div style={{ overflowX: "auto" }}>
						<table className="table table-sm" style={{ fontSize: 14 }}>
							<thead>
								<tr>
									<th>{Constants.Common.documentType}</th>
									<th>{Constants.Common.documentFile}</th>
									<th>{Constants.Common.documentDescription}</th>
									<th>{Constants.Common.uploadedBy}</th>
									<th>{Constants.Common.date}</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{documents.map((document) => (
									<tr key={"doc_" + document.id}>
										<td>{typeText(document)}</td>
										<td style={{ wordBreak: "break-all" }}>{document.name} <span style={{ color: "var(--wa-apagado)", fontSize: 12 }}>{sizeText(document.size)}</span></td>
										<td>{document.description || "-"}{history(document)}</td>
										<td>{document.createdBy || "-"}</td>
										<td style={{ whiteSpace: "nowrap" }}>{formatLocalDateTime(document.createdAt)}</td>
										<td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
											<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => view(document)}>
												<FontAwesomeIcon icon={solid("eye")} />&nbsp;{Constants.Common.view}
											</button>
											{document.contract && document.contract.canUploadSigned ? <>&nbsp;{signedButton(document, true)}</> : ""}
											{canUpload ? (
												<>&nbsp;<button type="button" className="btn btn-outline-danger btn-sm rounded-pill" disabled={busy} onClick={() => remove(document)}>
													<FontAwesomeIcon icon={solid("trash")} />
												</button></>
											) : ""}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				))}
		</div>
	);
};

export default StudentDocumentsTab;
